/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';
import { forwardRef, useRef } from 'react';
import { useSelector } from 'react-redux';

import { ChatResponseField, type ChatResponseFieldRef } from '@coursera-web/cds-labs';
import classNames from 'classnames';

import { useTracker } from '@coursera/event-pulse/react';

import type { CustomSendButtonProps, SendMessageFunc } from 'bundles/ai-coach-platform/components/patterns/chat/types';
import { CoachAction } from 'bundles/ai-coach-platform/constants';
import { useCoachTrackingContext } from 'bundles/ai-coach-platform/utils/tracking';

import _t from 'i18n!nls/ai-coach-platform';

import { clearUserInputText } from './store/dispatcher';
import type { GlobalState } from './store/types';

type Props = {
  placeholder: string;
  inputLabel?: string;
  disableInput: boolean;
  autofocus?: boolean;
  sendMessage: SendMessageFunc;
  onTextInputChange?: (text: string) => void;
  /**
   * @deprecated Temporary prop for the Two-Panel XDP experiment (MERCH-2963). No longer functional
   * after the cds-labs-chat-components gate cleanup. Will be removed once consumers are updated.
   */
  renderCustomSendButton?: (props: CustomSendButtonProps) => React.ReactElement | null;
};

const styles = {
  container: css`
    padding: 0 var(--cds-spacing-200);
    margin-bottom: 1px;
  `,
  cdsLabsInputField: css`
    padding-top: var(--cds-spacing-200);
  `,
};

const ChatInputField = (
  { sendMessage, placeholder, disableInput, autofocus = true, onTextInputChange, inputLabel }: Props,
  ref: React.Ref<HTMLInputElement>
) => {
  const { isMessageLoading } = useSelector((state: GlobalState) => ({
    isMessageLoading: state.behavior.messageLoader,
  }));

  const responseFieldRef = useRef<ChatResponseFieldRef>(null);
  const refContainer = useRef<HTMLDivElement>(null);
  const track = useTracker();
  const trackingContext = useCoachTrackingContext()?.data;

  const trackSendMessage = (message: string) => {
    if (trackingContext) {
      track('interact_coach', {
        coachInteractionType: 'send_message',
        coach: {
          mode: trackingContext.mode,
          location: 'chat',
        },
        ...(trackingContext.courseId && {
          product: {
            type: 'course',
            id: trackingContext.courseId as string,
          },
        }),
        ...(trackingContext.itemId && {
          courseItem: {
            id: trackingContext.itemId as string,
            type: trackingContext.itemType,
          },
        }),
        ...(trackingContext.form && { form: trackingContext.form }),
      });

      // This allows us to send the input message to amplitude as a separate event all together.
      // The main goal for now being to derive the character count of the input text as requested by product.
      track('send_coach_message', {
        coachMessageText: message,
        coachMessageLength: message.length,
        coach: {
          mode: trackingContext.mode,
          location: 'chat',
        },
        ...(trackingContext.courseId && {
          product: {
            type: 'course',
            id: trackingContext.courseId as string,
          },
        }),
        ...(trackingContext.itemId && {
          courseItem: {
            id: trackingContext.itemId as string,
            type: trackingContext.itemType,
          },
        }),
        ...(trackingContext.form && { form: trackingContext.form }),
      });
    }
  };

  const isDisabled = isMessageLoading || disableInput;

  const handleResponseFieldSend = (sendValue: string) => {
    if (!sendValue) return;
    sendMessage(sendValue, CoachAction.TypedMessage);
    trackSendMessage(sendValue);
    clearUserInputText();
    responseFieldRef.current?.clear();
  };

  return (
    <div ref={refContainer} css={styles.container}>
      <div
        css={styles.cdsLabsInputField}
        // Note: this className is needed since it's targeted by consumers in the non cds-labs path too.
        className={classNames('coach-chat-input-field', {
          'coach-message-disabled': isMessageLoading || disableInput,
        })}
      >
        <ChatResponseField
          ref={responseFieldRef}
          placeholder={placeholder}
          onSend={handleResponseFieldSend}
          onChange={onTextInputChange}
          disabled={isDisabled}
          hideSpeechToTextButton
          autoFocus={disableInput ? false : autofocus}
          data-testid="coach-send-message"
          aria-label={inputLabel ?? _t('Ask Coursera AI Chat')}
        />
      </div>
    </div>
  );
};

export default forwardRef(ChatInputField);
